// セッション(設計書 §3.4.4 / §3.4.5)の React Query フック

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiClientError, apiRequest, apiRequestRaw } from './client';

export type SessionState = 'starting' | 'running' | 'stopping' | 'completed' | 'failed';

export interface IceServer {
  urls: string;
  username?: string;
  credential?: string;
}

export interface CreateSessionResponse {
  session_ulid: string;
  state: 'starting';
  webrtc: {
    signaling_url: string;
    room_id: string;
    push_id: string;
    push_jwt: string;
    push_jwt_expires_in: number;
    view_jwt: string;
    view_jwt_expires_in: number;
    ice_servers: IceServer[];
  };
  assigned_server: {
    ulid: string;
    name: string;
    slot: 'A' | 'B';
  };
}

export interface BrowserVideoSettings {
  device_id?: string;
  width: number;
  height: number;
  framerate: number;
  max_bitrate_kbps: number;
}

export interface BrowserAudioSettings {
  device_id?: string;
  echo_cancellation: boolean;
  noise_suppression: boolean;
  auto_gain_control: boolean;
}

export interface CreateSessionInput {
  streaming_profile_ulid: string;
  browser_settings: {
    video: BrowserVideoSettings;
    audio: BrowserAudioSettings;
  };
}

export interface SessionDetail {
  ulid: string;
  state: SessionState;
  streaming_profile_ulid: string;
  started_at: string;
  running_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  termination_reason: string | null;
  error_message: string | null;
}

export const sessionKeys = {
  all: ['sessions'] as const,
  current: ['sessions', 'current'] as const,
  history: (params: HistoryQuery) => ['sessions', 'history', params] as const,
};

const ACTIVE_STATES: SessionState[] = ['starting', 'running', 'stopping'];

// 設計書 §3.4.4 GET /v1/sessions/current
// API は「配信中セッションが存在しない」状態を 404 で返す(これは正常状態)。
// ここで 404 を捕まえて null に正規化することで、呼び出し側 (StreamingPage)
// は `data === null` を「セッションなし」として扱える。それ以外の例外
// (5xx / ネットワークエラー等)は通常通り throw して error として扱う。
export function useCurrentSession() {
  return useQuery<SessionDetail | null>({
    queryKey: sessionKeys.current,
    queryFn: async () => {
      try {
        return await apiRequest<SessionDetail>('/sessions/current');
      } catch (e) {
        if (e instanceof ApiClientError && e.status === 404) {
          return null;
        }
        throw e;
      }
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && ACTIVE_STATES.includes(data.state)) {
        return data.state === 'running' ? 8_000 : 2_000;
      }
      return false;
    },
    // 5xx 等は 1 回だけリトライ。404 は queryFn 内で null に正規化済みなので
    // ここに到達しない(retry の対象にもならない)。
    retry: (failureCount, error) => {
      if (error instanceof ApiClientError && error.status === 404) {
        return false;
      }
      return failureCount < 1;
    },
  });
}

export function useCreateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSessionInput) =>
      apiRequest<CreateSessionResponse>('/sessions', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sessionKeys.current });
      void qc.invalidateQueries({ queryKey: sessionKeys.all });
    },
  });
}

export function useStopSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ulid: string) =>
      apiRequest<{ session_ulid: string; state: 'stopping' }>(`/sessions/${ulid}/stop`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sessionKeys.current });
      void qc.invalidateQueries({ queryKey: sessionKeys.all });
    },
  });
}

export interface HistoryQuery {
  page?: number;
  per_page?: number;
  streaming_profile_ulid?: string;
  state?: SessionState;
  from?: string;
  to?: string;
}

export interface SessionHistoryResponse {
  data: SessionDetail[];
  pagination: {
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
}

export function useSessionHistory(params: HistoryQuery) {
  return useQuery({
    queryKey: sessionKeys.history(params),
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.page !== undefined) qs.set('page', String(params.page));
      if (params.per_page !== undefined) qs.set('per_page', String(params.per_page));
      if (params.streaming_profile_ulid)
        qs.set('streaming_profile_ulid', params.streaming_profile_ulid);
      if (params.state) qs.set('state', params.state);
      if (params.from) qs.set('from', params.from);
      if (params.to) qs.set('to', params.to);
      const path = qs.toString() ? `/sessions?${qs.toString()}` : '/sessions';
      // /sessions は {data, pagination} のトップレベル並びなので raw 経由で受け取る
      return await apiRequestRaw<SessionHistoryResponse>(path);
    },
    placeholderData: (prev) => prev,
  });
}

export const SESSION_STATE_LABELS: Record<SessionState, string> = {
  starting: '開始中',
  running: '配信中',
  stopping: '停止処理中',
  completed: '完了',
  failed: '失敗',
};
